import { LanguagesEnum } from '@/api/enums';
import { Slide, SlideTemplateType } from '@/client/courses';
import { PopupSlideTemplateContent } from '@/components/slides/learner/slide-templates/PopupSlideTemplateContent';
import hardcodedTranslations from '@/locales/hardcodedTranslations';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { isMobile } from '@/utils/helpers/ui.helper';
import { Dialog } from 'primereact/dialog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const InfoBoxPreview = styled(AppButton)<{
  clickable?: boolean;
  isSummary?: boolean;
  rtl?: boolean;
}>`
  direction: ${({ rtl }) => (rtl ? 'rtl' : 'ltr')};
  &.p-button&.p-component {
    width: 100%;
    border-radius: var(--xsmall-border-radius);
    background: ${({ isSummary }) =>
      isSummary
        ? 'color-mix(in srgb, var(--blue-dark) 30%, transparent)'
        : 'var(--blue-light)'} !important;
    padding: 18px 22px;
    color: ${({ isSummary }) =>
      isSummary ? 'var(--white-main)' : 'var(--black-main)'} !important;

    &.p-disabled {
      opacity: 1 !important;

      .p-button-label {
        color: ${({ isSummary }) =>
          isSummary ? 'var(--white-main)' : 'var(--black-main)'} !important;
        opacity: 1 !important;
      }
    }
    font-size: var(--xsmall-font-size);
    margin: var(--default-padding) 0;
    display: flex;
    align-items: center;
    text-align: start;
    gap: 10px;
    border: none !important;
    ${({ clickable }) =>
      clickable &&
      `
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background-color: ${({ isSummary }: { isSummary: boolean }) =>
          isSummary
            ? 'color-mix(in srgb, var(--blue-dark) 30%, transparent)'
            : 'var(--blue-light)'} !important;
        opacity: 0.8 !important;
      }
    `}

    .p-button-label {
      color: ${({ isSummary }) =>
        isSummary ? 'var(--white-main)' : 'var(--black-main)'} !important;
    }

    &.p-button-text {
      font-size: var(--small-font-size);
      line-height: 130%;
    }
  }
`;

const InfoBoxIcon = styled.span`
  display: flex;
  align-items: center;

  i {
    font-size: var(--semi-big-font-size);
    color: var(--blue-dark);
  }
`;

const StyledDialog = styled(Dialog)<{ isPurpleBackground?: boolean }>`
  &.p-dialog,
  &.p-component,
  .p-dialog-content,
  .p-dialog-header,
  .p-dialog-footer {
    background: ${({ isPurpleBackground }) =>
      isPurpleBackground
        ? 'var(--purple-dark)'
        : 'var(--beige-main)'} !important;
    color: ${({ isPurpleBackground }) =>
      isPurpleBackground
        ? 'var(--white-main)'
        : 'var(--black-main)'} !important;
  }
`;

export type InfoBoxProps = {
  text: string;
  modalContent?: Slide;
  className?: string;
  showIcon?: boolean;
  iconClassName?: string;
  isSummary?: boolean;
  rtl?: boolean;
  attemptLanguage?: LanguagesEnum;
};

export const InfoBox: React.FC<InfoBoxProps> = ({
  text,
  modalContent,
  className,
  showIcon = true,
  iconClassName = 'pi pi-info-circle',
  isSummary = false,
  rtl = false,
  attemptLanguage,
}) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const isPurpleBackground =
    modalContent?.templateType === SlideTemplateType.SUMMARY;

  const isClickable = !!modalContent;

  const handleClick = () => {
    if (isClickable) {
      setShowModal(true);
    }
  };

  return (
    <>
      <InfoBoxPreview
        className={className}
        clickable={isClickable}
        isDisabled={!isClickable}
        onClick={handleClick}
        isSummary={isSummary}
        rtl={rtl}
        icon={
          showIcon && (
            <InfoBoxIcon>
              <i className={iconClassName} />
            </InfoBoxIcon>
          )
        }
        label={text}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
        tabIndex={0}
      />

      {modalContent && (
        <StyledDialog
          visible={showModal}
          onHide={() => setShowModal(false)}
          className="v2"
          style={{ width: isMobile() ? '95vw' : '1000px', maxWidth: '95vw' }}
          draggable={false}
          closable={false}
          closeOnEscape={true}
          blockScroll
          modal
          isPurpleBackground={isPurpleBackground}
        >
          <FlexContainer
            direction="column"
            align="flex-start"
            height="100%"
            dir={rtl ? 'rtl' : undefined}
          >
            <div className="pb-5 w-full">
              <PopupSlideTemplateContent slide={modalContent} rtl={rtl} />
            </div>

            <AppButton
              autoFocus
              label={
                attemptLanguage
                  ? hardcodedTranslations.buttons.closeInfoBox[attemptLanguage]
                  : t('content.close.info.box')
              }
              onClick={() => setShowModal(false)}
            />
          </FlexContainer>
        </StyledDialog>
      )}
    </>
  );
};
